import { useCallback, useEffect, useRef, useState } from "react";
import useFileUpload, { type FileExtractionEvent } from "@/hooks/useFileUpload";

export type IngestStage = "queued" | "uploading" | "parsing" | "chunking" | "embedding" | "indexed" | "error";

export interface FileIngestState {
    id: string;
    name: string;
    sizeBytes: number;
    stage: IngestStage;
    progress: number;
    overallProgress: number;
    chunks: number | null;
    vectors: number | null;
    fileId: number | null;
    errorReason: string | null;
}

export type IngestionBatchStatus = "idle" | "in_progress" | "complete" | "partial";

export interface UseIngestionBatch {
    files: FileIngestState[];
    status: IngestionBatchStatus;
    batchProgress: number;
    indexedCount: number;
    failedCount: number;
    addFiles: (files: File[]) => void;
    removeFile: (id: string) => void;
    retryFile: (id: string) => void;
}

export const MAX_FILE_BYTES = 50 * 1024 * 1024;
export const MAX_BATCH_FILES = 20;

// Pipeline bands for the aggregate bar: upload occupies 0–30, extraction 30–100.
const UPLOAD_BAND = 30;

function stageForSseStatus(status: string): IngestStage | null {
    switch (status) {
        case "started":
        case "downloading":
        case "converting":
        case "saving_text":
            return "parsing";
        case "chunking":
        case "chunked":
        case "saving_metadata":
            return "chunking";
        case "processing_chunks":
        case "cleaning":
        case "embedding_chunk":
        case "committing":
            return "embedding";
        case "completed":
            return "indexed";
        default:
            return null;
    }
}

function friendlyError(raw: string | null): string {
    if (raw && /\b401\b/.test(raw)) return "Session expired — please retry.";
    return raw ?? "Upload failed";
}

export function useIngestionBatch(opts?: {
    initialFiles?: File[];
    /** Current product/project id (KAN-1584 follow-up), threaded into
     *  useFileUpload so uploads auto-scope to the current product. Omit when
     *  there's no current product — files save unscoped, same as today. */
    projectId?: number;
    onFileIndexed?: (r: { fileId: number; filename: string }) => void;
    onSettled?: (summary: {
        indexed: { fileId: number; filename: string }[];
        failed: { filename: string; error: string }[];
    }) => void;
    /** Fired at most once, only if the batch was still `in_progress` when the
     *  hook unmounts (e.g. the chat session was cleared/switched mid-batch).
     *  Computed from the render-synced file list at unmount time — the file
     *  that was uploading/extracting is never counted as indexed even if the
     *  in-flight request happens to resolve after teardown, because the
     *  queue runner's `.then` no-ops once `mountedRef.current` is false. */
    onAborted?: (summary: {
        indexed: { fileId: number; filename: string }[];
        incomplete: string[];
    }) => void;
}): UseIngestionBatch {
    const [files, setFiles] = useState<FileIngestState[]>([]);
    const filesRef = useRef<FileIngestState[]>([]);
    filesRef.current = files;
    const fileObjectsRef = useRef(new Map<string, File>());
    const seqRef = useRef(0);
    const activeIdRef = useRef<string | null>(null);
    const lastErrorRef = useRef<string | null>(null);
    const settledRef = useRef(false);
    const mountedRef = useRef(true);
    const abortControllerRef = useRef<AbortController | null>(null);
    const optsRef = useRef(opts);
    optsRef.current = opts;

    const patchFile = useCallback((id: string, patch: Partial<FileIngestState>) => {
        setFiles(prev => prev.map(f => (f.id === id ? { ...f, ...patch } : f)));
    }, []);

    const { handleUpload } = useFileUpload({
        projectId: opts?.projectId,
        onUploadProgress: progress => {
            const id = activeIdRef.current;
            if (!id) return;
            patchFile(id, { stage: "uploading", progress, overallProgress: Math.round((progress * UPLOAD_BAND) / 100) });
        },
        onSavingFileToDatabaseStart: () => {
            const id = activeIdRef.current;
            if (!id) return;
            patchFile(id, { stage: "parsing", progress: 0, overallProgress: UPLOAD_BAND });
        },
        onFileTextExtractionEvent: (_name, event: FileExtractionEvent) => {
            const id = activeIdRef.current;
            if (!id) return;
            const stage = stageForSseStatus(event.status);
            if (!stage || stage === "indexed") return; // "indexed" is set on handleUpload resolution
            const patch: Partial<FileIngestState> = {
                stage,
                progress: event.progress ?? 0,
                overallProgress: UPLOAD_BAND + Math.round(((event.progress ?? 0) * (100 - UPLOAD_BAND)) / 100)
            };
            if (typeof event.total_chunks === "number") patch.chunks = event.total_chunks;
            if (stage === "embedding" && typeof event.current_chunk === "number") patch.vectors = event.current_chunk;
            patchFile(id, patch);
        },
        onError: message => {
            lastErrorRef.current = message;
        }
    });

    const toState = useCallback((file: File): FileIngestState | null => {
        const id = `f${seqRef.current++}`;
        if (file.size > MAX_FILE_BYTES) {
            // Over-limit: Error row without a pipeline slot (design §Limits).
            return {
                id, name: file.name, sizeBytes: file.size,
                stage: "error", progress: 0, overallProgress: 0,
                chunks: null, vectors: null, fileId: null,
                errorReason: "File too large — 50 MB max"
            };
        }
        fileObjectsRef.current.set(id, file);
        return {
            id, name: file.name, sizeBytes: file.size,
            stage: "queued", progress: 0, overallProgress: 0,
            chunks: null, vectors: null, fileId: null, errorReason: null
        };
    }, []);

    const addFiles = useCallback((incoming: File[]) => {
        // Compute the accepted rows (and their id/File-object side effects) eagerly,
        // outside the setFiles updater. React (StrictMode) may invoke a functional
        // update twice to check for impurity; keeping the updater pure — just
        // appending a precomputed array — means a duplicate invocation is harmless.
        const room = MAX_BATCH_FILES - filesRef.current.length;
        if (incoming.length > room) {
            console.warn(`[useIngestionBatch] batch limited to ${MAX_BATCH_FILES} files; dropping ${incoming.length - Math.max(room, 0)}`);
        }
        const accepted = incoming.slice(0, Math.max(room, 0));
        const next = accepted.map(toState).filter((f): f is FileIngestState => f !== null);
        settledRef.current = false;
        setFiles(prev => [...prev, ...next]);
    }, [toState]);

    // Seed initial files exactly once.
    const seededRef = useRef(false);
    useEffect(() => {
        if (seededRef.current) return;
        seededRef.current = true;
        if (opts?.initialFiles?.length) addFiles(opts.initialFiles);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const removeFile = useCallback((id: string) => {
        setFiles(prev => {
            const target = prev.find(f => f.id === id);
            if (!target || target.stage !== "queued") return prev; // v1: queued rows only
            fileObjectsRef.current.delete(id);
            return prev.filter(f => f.id !== id);
        });
    }, []);

    const retryFile = useCallback((id: string) => {
        setFiles(prev => {
            const target = prev.find(f => f.id === id);
            if (!target || target.stage !== "error" || !fileObjectsRef.current.has(id)) return prev;
            settledRef.current = false;
            return prev.map(f =>
                f.id === id
                    ? { ...f, stage: "queued" as IngestStage, progress: 0, overallProgress: 0, errorReason: null }
                    : f
            );
        });
    }, []);

    // Sequential queue runner: start the next queued file whenever nothing is active.
    useEffect(() => {
        if (activeIdRef.current !== null) return;
        const next = files.find(f => f.stage === "queued");
        if (!next) return;
        const file = fileObjectsRef.current.get(next.id);
        if (!file) return;
        activeIdRef.current = next.id;
        lastErrorRef.current = null;
        patchFile(next.id, { stage: "uploading", progress: 0 });
        const controller = new AbortController();
        abortControllerRef.current = controller;
        void handleUpload(file, { signal: controller.signal }).then(fileId => {
            if (!mountedRef.current) return; // unmounted mid-flight: no state, no callbacks
            const id = next.id;
            activeIdRef.current = null;
            if (fileId != null) {
                setFiles(prev => prev.map(f =>
                    f.id === id
                        ? { ...f, stage: "indexed", progress: 100, overallProgress: 100, fileId, vectors: f.chunks }
                        : f
                ));
                optsRef.current?.onFileIndexed?.({ fileId, filename: next.name });
            } else {
                patchFile(id, { stage: "error", errorReason: friendlyError(lastErrorRef.current) });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files, handleUpload, patchFile]);

    // Unmount cleanup: registered once. The setup body resets mountedRef so
    // StrictMode's synthetic mount→cleanup→mount cycle doesn't permanently
    // wedge mountedRef to false before the "real" mount settles in. Only the
    // cleanup (the real unmount) aborts in-flight work and — if the batch was
    // still unresolved — reports what got interrupted, read from `filesRef`
    // (kept render-synced above) rather than the stale `files` this closure
    // captured at effect-registration time.
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            abortControllerRef.current?.abort();
            const currentFiles = filesRef.current;
            const stillUnresolved = currentFiles.some(f => f.stage !== "indexed" && f.stage !== "error");
            if (currentFiles.length === 0 || !stillUnresolved) return;
            optsRef.current?.onAborted?.({
                indexed: currentFiles.filter(f => f.stage === "indexed").map(f => ({ fileId: f.fileId as number, filename: f.name })),
                incomplete: currentFiles.filter(f => f.stage !== "indexed" && f.stage !== "error").map(f => f.name)
            });
        };
    }, []);

    const indexedCount = files.filter(f => f.stage === "indexed").length;
    const failedCount = files.filter(f => f.stage === "error").length;
    const unresolved = files.some(f => f.stage !== "indexed" && f.stage !== "error");
    const status: IngestionBatchStatus =
        files.length === 0 ? "idle" : unresolved ? "in_progress" : failedCount > 0 ? "partial" : "complete";
    const batchProgress = files.length === 0
        ? 0
        : Math.round(files.reduce((sum, f) => sum + (f.stage === "error" ? 100 : f.overallProgress), 0) / files.length);

    // Fire onSettled exactly once per settle.
    useEffect(() => {
        if (status !== "complete" && status !== "partial") return;
        if (settledRef.current) return;
        settledRef.current = true;
        optsRef.current?.onSettled?.({
            indexed: files.filter(f => f.stage === "indexed").map(f => ({ fileId: f.fileId as number, filename: f.name })),
            failed: files.filter(f => f.stage === "error").map(f => ({ filename: f.name, error: f.errorReason ?? "Upload failed" }))
        });
    }, [status, files]);

    return { files, status, batchProgress, indexedCount, failedCount, addFiles, removeFile, retryFile };
}
